/** Concatenates the chunks a stream emits into one contiguous buffer. */
async function drain(stream: ReadableStream<Uint8Array>): Promise<Uint8Array> {
  const reader = stream.getReader()
  const chunks: Uint8Array[] = []
  let total = 0

  for (;;) {
    const { done, value } = await reader.read()
    if (done) break
    chunks.push(value)
    total += value.length
  }

  const out = new Uint8Array(total)
  let offset = 0
  for (const chunk of chunks) {
    out.set(chunk, offset)
    offset += chunk.length
  }
  return out
}

/**
 * Compresses a value to gzipped JSON bytes.
 *
 * Uses the platform's own `CompressionStream` rather than a library: it is
 * available in every browser PF-Connect supports, and the repo's `.npmrc` sets
 * `min-release-age = 7`, so any new dependency would be unresolvable for a week
 * after publication.
 *
 * Built from a `ReadableStream` and `TextEncoder` rather than `Blob.stream()`
 * and `Response`, which keeps the dependency surface to the two APIs actually
 * needed for compression.
 *
 * Compression happens per bundle member, not over the whole bundle. The
 * screenshot is already PNG, so re-compressing it would only cost CPU, and a
 * single archive would force the receiver to unpack before it could show
 * anything.
 */
export async function gzipJson(value: unknown): Promise<Uint8Array> {
  const bytes = new TextEncoder().encode(JSON.stringify(value))
  const source = new ReadableStream<Uint8Array<ArrayBuffer>>({
    start(controller): void {
      controller.enqueue(bytes)
      controller.close()
    },
  })

  return drain(source.pipeThrough(new CompressionStream('gzip')))
}
